import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './components/ui/table';
import { Progress } from './components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './components/ui/dialog';
import { RadioGroup, RadioGroupItem } from './components/ui/radio-group';
import { Textarea } from './components/ui/textarea';
import { toast } from 'sonner';
import { Trash2, Users, Calendar, DollarSign, UserPlus, Mail, Settings, FolderOpen } from 'lucide-react';
import HomeworkManager from './components/HomeworkManager';
import ExerciseManager from './components/ExerciseManager';
import LoreContentManager from './components/LoreContentManager';
import WorkbookContentManager from './components/WorkbookContentManager';
import CSVImportExport from './components/CSVImportExport';
import AnnouncementManager from './components/AnnouncementManager';
import ExperimentalFeatures from './components/ExperimentalFeatures';
import EventManager from './components/EventManager';
import EbookHighlightManager from './components/EbookHighlightManager';
import BlogPostManager from './components/BlogPostManager';
import ResourcesManager from './components/ResourcesManager';

function Admin() {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [token, setToken] = useState(localStorage.getItem('admin_token'));
  const [registrations, setRegistrations] = useState([]);
  const [stats, setStats] = useState({ total: 0, intro: 0, full_series: 0, scholarships: 0 });
  const [seatInfo, setSeatInfo] = useState({});
  const [editingSeats, setEditingSeats] = useState(false);
  const [newSeats, setNewSeats] = useState({ intro_max_seats: 30, series_max_seats: 15 });
  const [aiEnabled, setAiEnabled] = useState(false);
  const [introWaitingListEnabled, setIntroWaitingListEnabled] = useState(false);
  const [seriesWaitingListEnabled, setSeriesWaitingListEnabled] = useState(false);
  const [autoSendRegistrationEmail, setAutoSendRegistrationEmail] = useState(true);
  const [autoSendWaitingListEmail, setAutoSendWaitingListEmail] = useState(true);
  const [showIntroOnLanding, setShowIntroOnLanding] = useState(true);
  const [loading, setLoading] = useState(false);
  const [contactSubmissions, setContactSubmissions] = useState([]);
  const [showNewsletterOnly, setShowNewsletterOnly] = useState(true);
  const [activeTab, setActiveTab] = useState('registrations');

  // Filter registrations
  const filteredRegistrations = showNewsletterOnly 
    ? registrations 
    : registrations.filter(r => r.attending !== 'newsletter');
  const [waitingList, setWaitingList] = useState([]);
  const [editingReg, setEditingReg] = useState(null);
  const [editForm, setEditForm] = useState({
    full_name: '',
    email: '',
    phone: '',
    attending: 'intro',
    price_selected: '175',
    referral: '',
    payment_status: 'pending',
    payment_method: '',
    in_person: false
  });
  const [addUserDialogOpen, setAddUserDialogOpen] = useState(false);
  const [newUserData, setNewUserData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    attending: 'intro',
    price_selected: 'free',
    referral: '',
    payment_status: 'pending',
    payment_method: '',
    in_person: false
  });
  const [emailData, setEmailData] = useState({
    to_emails: '',
    subject: '',
    message: '',
    is_html: false
  });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState({
    registration: { subject: '', body: '' },
    waiting_list: { subject: '', body: '' }
  });
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [smtpSettings, setSmtpSettings] = useState({
    smtp_host: '',
    smtp_port: 465,
    smtp_username: '',
    smtp_password: '',
    smtp_from_email: '',
    smtp_from_name: '',
    admin_notification_emails: []
  });
  const [showPassword, setShowPassword] = useState(false);
  const [emailLogs, setEmailLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [emailLogFilter, setEmailLogFilter] = useState('all'); // 'all', 'success', 'failed'
  const [loadingFeedback, setLoadingFeedback] = useState(false);
  const [feedbackList, setFeedbackList] = useState([]);

  useEffect(() => {
    if (token) {
      setIsAuthenticated(true);
      fetchRegistrations();
      fetchSeatInfo();
      fetchContactSubmissions();
      fetchWaitingList();
      fetchEmailTemplates();
      fetchSMTPSettings();
      fetchEmailLogs();
      fetchFeedback();
      
      // Check for new activity every 30 seconds
      const interval = setInterval(() => {
        checkForNewActivity();
      }, 30000);
      
      return () => clearInterval(interval);
    }
  }, [token]);

  const checkForNewActivity = async () => {
    try {
      // Check for new registrations
      const regResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/registrations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (regResponse.ok) {
        const data = await regResponse.json();
        const newRegs = data.registrations.filter(r => 
          new Date(r.created_at) > new Date(Date.now() - 5*60*1000) // 5 minutes
        );
        if (newRegs.length > 0) {
          toast.success(`🎉 ${newRegs.length} new registration(s)! Check the Registrations tab.`, { duration: 8000 });
        }
      }

      // Check for new contacts
      const contactResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/contact-requests`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (contactResponse.ok) {
        const data = await contactResponse.json();
        const newContacts = data.contacts.filter(c => 
          new Date(c.created_at) > new Date(Date.now() - 5*60*1000)
        );
        if (newContacts.length > 0) {
          toast.info(`📝 ${newContacts.length} new contact submission(s)! Check Contact Submissions tab.`, { duration: 8000 });
        }
      }
    } catch (error) {
      console.error('Activity check failed');
    }
  };

  const fetchContactSubmissions = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/contact-requests`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setContactSubmissions(data.contacts || []);
      }
    } catch (error) {
      console.error('Failed to fetch contacts');
    }
  };

  const fetchWaitingList = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/waiting-list`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setWaitingList(data.waiting_list || []);
      }
    } catch (error) {
      console.error('Failed to fetch waiting list');
    }
  };

  const fetchEmailTemplates = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/email-templates`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        const templates = {
          registration: { subject: '', body: '' },
          waiting_list: { subject: '', body: '' }
        };
        data.templates.forEach(t => {
          templates[t.template_type] = { subject: t.subject, body: t.body };
        });
        setEmailTemplates(templates);
      }
    } catch (error) {
      console.error('Failed to fetch email templates');
    }
  };

  const fetchSMTPSettings = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/smtp-settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSmtpSettings({
          smtp_host: data.smtp_host,
          smtp_port: data.smtp_port,
          smtp_username: data.smtp_username,
          smtp_password: '',
          smtp_from_email: data.smtp_from_email,
          smtp_from_name: data.smtp_from_name,
          admin_notification_emails: data.admin_notification_emails || []
        });
      }
    } catch (error) {
      console.error('Failed to fetch SMTP settings');
    }
  };

  const fetchEmailLogs = async () => {
    setLoadingLogs(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/email-logs?limit=100`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setEmailLogs(data.logs || []);
      }
    } catch (error) {
      console.error('Failed to fetch email logs');
    } finally {
      setLoadingLogs(false);
    }
  };

  const fetchFeedback = async () => {
    setLoadingFeedback(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/feedback`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setFeedbackList(data.feedback || []);
      }
    } catch (error) {
      console.error('Failed to fetch feedback');
    } finally {
      setLoadingFeedback(false);
    }
  };

  const fetchSeatInfo = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/events/settings`);
      if (response.ok) {
        const data = await response.json();
        setSeatInfo(data);
        setNewSeats({
          intro_max_seats: data.intro_max_seats,
          series_max_seats: data.series_max_seats
        });
        setAiEnabled(data.ai_features_enabled || false);
        setIntroWaitingListEnabled(data.intro_waiting_list_enabled || false);
        setSeriesWaitingListEnabled(data.series_waiting_list_enabled || false);
        setAutoSendRegistrationEmail(data.auto_send_registration_email !== false);
        setAutoSendWaitingListEmail(data.auto_send_waiting_list_email !== false);
        setShowIntroOnLanding(data.show_intro_on_landing !== false);
      }
    } catch (error) {
      console.error('Failed to fetch seat info');
    }
  };

  const toggleAI = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/events/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          intro_max_seats: seatInfo.intro_max_seats,
          series_max_seats: seatInfo.series_max_seats,
          intro_date: seatInfo.intro_date,
          intro_time: seatInfo.intro_time,
          series_dates: seatInfo.series_dates,
          ai_features_enabled: !aiEnabled,
          intro_waiting_list_enabled: introWaitingListEnabled,
          series_waiting_list_enabled: seriesWaitingListEnabled,
          auto_send_registration_email: autoSendRegistrationEmail,
          auto_send_waiting_list_email: autoSendWaitingListEmail,
          pricing_25: 25.00,
          pricing_50: 50.00,
          pricing_100: 100.00,
          pricing_150: 150.00,
          pricing_200: 200.00
        })
      });
      
      if (!response.ok) throw new Error('Update failed');
      
      setAiEnabled(!aiEnabled);
      toast.success(aiEnabled ? 'AI features disabled' : 'AI features enabled');
    } catch (error) {
      toast.error('Failed to toggle AI');
    }
  };

  const toggleIntroWaitingList = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/events/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          intro_max_seats: seatInfo.intro_max_seats,
          series_max_seats: seatInfo.series_max_seats,
          intro_date: seatInfo.intro_date,
          intro_time: seatInfo.intro_time,
          series_dates: seatInfo.series_dates,
          ai_features_enabled: aiEnabled,
          intro_waiting_list_enabled: !introWaitingListEnabled,
          series_waiting_list_enabled: seriesWaitingListEnabled,
          auto_send_registration_email: autoSendRegistrationEmail,
          auto_send_waiting_list_email: autoSendWaitingListEmail,
          pricing_25: 25.00,
          pricing_50: 50.00,
          pricing_100: 100.00,
          pricing_150: 150.00,
          pricing_200: 200.00
        })
      });
      
      if (!response.ok) throw new Error('Update failed');
      
      setIntroWaitingListEnabled(!introWaitingListEnabled);
      toast.success(introWaitingListEnabled ? 'Free Intro waiting list disabled' : 'Free Intro waiting list enabled - FULL');
    } catch (error) {
      toast.error('Failed to toggle intro waiting list');
    }
  };

  const toggleSeriesWaitingList = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/events/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          intro_max_seats: seatInfo.intro_max_seats,
          series_max_seats: seatInfo.series_max_seats,
          intro_date: seatInfo.intro_date,
          intro_time: seatInfo.intro_time,
          series_dates: seatInfo.series_dates,
          ai_features_enabled: aiEnabled,
          intro_waiting_list_enabled: introWaitingListEnabled,
          series_waiting_list_enabled: !seriesWaitingListEnabled,
          auto_send_registration_email: autoSendRegistrationEmail,
          auto_send_waiting_list_email: autoSendWaitingListEmail,
          pricing_25: 25.00,
          pricing_50: 50.00,
          pricing_100: 100.00,
          pricing_150: 150.00,
          pricing_200: 200.00
        })
      });
      
      if (!response.ok) throw new Error('Update failed');
      
      setSeriesWaitingListEnabled(!seriesWaitingListEnabled);
      toast.success(seriesWaitingListEnabled ? 'Full Series waiting list disabled' : 'Full Series waiting list enabled - FULL');
    } catch (error) {
      toast.error('Failed to toggle series waiting list');
    }
  };

  const toggleAutoRegistrationEmail = async () => {
    try {
      const newValue = !autoSendRegistrationEmail;
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/events/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          intro_max_seats: seatInfo.intro_max_seats,
          series_max_seats: seatInfo.series_max_seats,
          intro_date: seatInfo.intro_date,
          intro_time: seatInfo.intro_time,
          series_dates: seatInfo.series_dates,
          ai_features_enabled: aiEnabled,
          intro_waiting_list_enabled: introWaitingListEnabled,
          series_waiting_list_enabled: seriesWaitingListEnabled,
          auto_send_registration_email: newValue,
          auto_send_waiting_list_email: autoSendWaitingListEmail,
          pricing_25: 25.00,
          pricing_50: 50.00,
          pricing_100: 100.00,
          pricing_150: 150.00,
          pricing_200: 200.00
        })
      });
      
      if (!response.ok) throw new Error('Update failed');
      
      setAutoSendRegistrationEmail(newValue);
      toast.success(newValue ? 'Registration emails will auto-send' : 'Registration emails disabled');
    } catch (error) {
      toast.error('Failed to toggle registration emails');
    }
  };

  const toggleAutoWaitingListEmail = async () => {
    try {
      const newValue = !autoSendWaitingListEmail;
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/events/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          intro_max_seats: seatInfo.intro_max_seats,
          series_max_seats: seatInfo.series_max_seats,
          intro_date: seatInfo.intro_date,
          intro_time: seatInfo.intro_time,
          series_dates: seatInfo.series_dates,
          ai_features_enabled: aiEnabled,
          intro_waiting_list_enabled: introWaitingListEnabled,
          series_waiting_list_enabled: seriesWaitingListEnabled,
          auto_send_registration_email: autoSendRegistrationEmail,
          auto_send_waiting_list_email: newValue,
          pricing_25: 25.00,
          pricing_50: 50.00,
          pricing_100: 100.00,
          pricing_150: 150.00,
          pricing_200: 200.00
        })
      });
      
      if (!response.ok) throw new Error('Update failed');
      
      setAutoSendWaitingListEmail(newValue);
      toast.success(newValue ? 'Waiting list emails will auto-send' : 'Waiting list emails disabled');
    } catch (error) {
      toast.error('Failed to toggle waiting list emails');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      });

      if (!response.ok) throw new Error('Invalid credentials');

      const data = await response.json();
      localStorage.setItem('admin_token', data.access_token);
      localStorage.setItem('admin_username', username);
      setToken(data.access_token);
      setIsAuthenticated(true);
      toast.success(`Welcome back, ${username}!`);
    } catch (error) {
      toast.error('Invalid credentials');
    }
  };

  const exportToGoHighLevel = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/export-ghl`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Export failed');
      
      const data = await response.json();
      const blob = new Blob([JSON.stringify(data.contacts, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `gohighlevel-export-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      toast.success('GoHighLevel export downloaded');
    } catch (error) {
      toast.error('Export failed');
    }
  };

  const updateSeats = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/events/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          intro_max_seats: newSeats.intro_max_seats,
          series_max_seats: newSeats.series_max_seats,
          intro_date: seatInfo.intro_date,
          intro_time: seatInfo.intro_time,
          series_dates: seatInfo.series_dates,
          ai_features_enabled: aiEnabled,
          intro_waiting_list_enabled: introWaitingListEnabled,
          series_waiting_list_enabled: seriesWaitingListEnabled,
          pricing_25: 25.00,
          pricing_50: 50.00,
          pricing_100: 100.00,
          pricing_150: 150.00,
          pricing_200: 200.00
        })
      });
      
      if (!response.ok) throw new Error('Update failed');
      
      await fetchSeatInfo();
      setEditingSeats(false);
      toast.success('Seat limits updated');
    } catch (error) {
      toast.error('Failed to update seats');
    }
  };

  const fetchRegistrations = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/registrations`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Failed to fetch');

      const data = await response.json();
      setRegistrations(data.registrations);
      setStats(data.stats);
    } catch (error) {
      toast.error('Failed to load registrations');
    } finally {
      setLoading(false);
    }
  };

  const startEditing = (reg) => {
    setEditingReg(reg.id);
    setEditForm({
      full_name: reg.full_name || '',
      email: reg.email || '',
      phone: reg.phone || '',
      attending: reg.attending || 'intro',
      price_selected: reg.price_selected || '175',
      referral: reg.referral || '',
      payment_status: reg.payment_status || 'pending',
      payment_method: reg.payment_method || '',
      in_person: reg.in_person || false
    });
  };

  const saveEdit = async (regId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/registrations/${regId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editForm)
      });

      if (!response.ok) throw new Error('Update failed');

      toast.success('Registration updated!');
      setEditingReg(null);
      fetchRegistrations();
    } catch (error) {
      toast.error('Failed to update registration');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this registration?')) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/registrations/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Delete failed');

      toast.success('Registration deleted');
      fetchRegistrations();
    } catch (error) {
      toast.error('Failed to delete registration');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    setToken(null);
    setIsAuthenticated(false);
    setRegistrations([]);
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/registrations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newUserData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to add user');
      }

      toast.success('User added successfully!');
      setAddUserDialogOpen(false);
      setNewUserData({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        attending: 'intro',
        price_selected: '175',
        referral: '',
        payment_status: 'pending',
        payment_method: '',
        in_person: false,
        comments: ''
      });
      fetchRegistrations();
    } catch (error) {
      console.error('Add user error:', error);
      toast.error(`Failed to add user: ${error.message}`);
    }
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();
    setSendingEmail(true);
    
    try {
      // Parse email addresses (comma or newline separated)
      const emails = emailData.to_emails
        .split(/[,\n]/)
        .map(e => e.trim())
        .filter(e => e.length > 0);
      
      if (emails.length === 0) {
        toast.error('Please enter at least one email address');
        setSendingEmail(false);
        return;
      }
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/send-email`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          to_emails: emails,
          subject: emailData.subject,
          message: emailData.message,
          is_html: emailData.is_html
        })
      });

      if (!response.ok) throw new Error('Failed to send email');

      const data = await response.json();
      toast.success(data.message);
      
      // Reset form
      setEmailData({
        to_emails: '',
        subject: '',
        message: '',
        is_html: false
      });
      setSelectedUsers([]);
    } catch (error) {
      toast.error('Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  const toggleUserSelection = (email) => {
    setSelectedUsers(prev => {
      if (prev.includes(email)) {
        return prev.filter(e => e !== email);
      } else {
        return [...prev, email];
      }
    });
  };

  const selectAllUsers = () => {
    if (!registrations || registrations.length === 0) {
      return;
    }
    if (selectedUsers.length === registrations.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(registrations.map(r => r.email));
    }
  };

  const emailSelected = () => {
    if (selectedUsers.length === 0) {
      toast.error('Please select at least one user');
      return;
    }
    
    // Pre-fill email addresses
    setEmailData(prev => ({
      ...prev,
      to_emails: selectedUsers.join('\n')
    }));
    
    toast.success(`${selectedUsers.length} email(s) added. Switch to 'Send Email' tab to compose message.`);
  };

  const saveEmailTemplate = async (templateType) => {
    try {
      const template = emailTemplates[templateType];
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/email-templates/${templateType}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          template_type: templateType,
          subject: template.subject,
          body: template.body
        })
      });

      if (!response.ok) throw new Error('Failed to save template');

      toast.success('Email template saved!');
      setEditingTemplate(null);
    } catch (error) {
      toast.error('Failed to save template');
    }
  };

  const saveSMTPSettings = async () => {
    try {
      // Convert admin emails string to array
      const emailList = typeof smtpSettings.admin_notification_emails === 'string' 
        ? smtpSettings.admin_notification_emails.split(',').map(e => e.trim()).filter(e => e)
        : smtpSettings.admin_notification_emails;

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/smtp-settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...smtpSettings,
          admin_notification_emails: emailList
        })
      });

      if (!response.ok) throw new Error('Failed to save SMTP settings');

      toast.success('SMTP settings saved! Emails will now use these credentials.');
      setShowPassword(false);
    } catch (error) {
      toast.error('Failed to save SMTP settings');
    }
  };

  const exportCSV = () => {
    const headers = ['Name', 'Email', 'Phone', 'Session', 'Price', 'Referral', 'Date'];
    const rows = registrations.map(r => [
      r.full_name || '',
      r.email || '',
      r.phone || '',
      r.attending || 'intro',
      r.price_selected || '175',
      r.referral || '',
      new Date(r.created_at).toLocaleDateString()
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `belonging-sessions-registrations-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    toast.success('CSV exported');
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        {/* Background texture */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(212, 175, 55, 0.4) 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }}></div>
        </div>
        <Card className="w-full max-w-md bg-stone-900 border border-amber-500/30 relative z-10" style={{ borderRadius: 0 }}>
          <CardHeader className="border-b border-stone-800">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-amber-700 rounded-lg flex items-center justify-center">
                <span className="text-black font-bold text-lg">B</span>
              </div>
              <div>
                <CardTitle className="text-2xl font-light text-white" style={{ fontFamily: 'Playfair Display, serif' }}>Admin Login</CardTitle>
                <p className="text-amber-500/70 text-xs tracking-wide">THE BELONGING SESSIONS</p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-stone-400">Username</Label>
                <Input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="steve, tony, or carrie"
                  required
                  style={{ borderRadius: 0 }}
                  className="bg-stone-800/50 border-stone-700 text-white placeholder:text-stone-500 focus:border-amber-500"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-stone-400">Password</Label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  style={{ borderRadius: 0 }}
                  className="bg-stone-800/50 border-stone-700 text-white placeholder:text-stone-500 focus:border-amber-500"
                />
              </div>
              <Button 
                type="submit" 
                className="w-full bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-500 hover:to-amber-600 text-black font-semibold py-6"
                style={{ borderRadius: 0 }}
              >
                Login
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white dark-admin">
      {/* Background texture */}
      <div className="fixed inset-0 opacity-5 pointer-events-none">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(212, 175, 55, 0.4) 1px, transparent 0)',
          backgroundSize: '40px 40px'
        }}></div>
      </div>
      
      {/* Single Tabs Wrapper for Entire Admin */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full min-h-screen relative z-10">

      {/* Compact Dark Header - NOT sticky */}
      <header className="bg-stone-900/95 border-b border-amber-500/20">
        <div className="max-w-7xl mx-auto px-4">
          {/* Compact top bar with logo and actions */}
          <div className="flex justify-between items-center py-2">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 bg-gradient-to-br from-amber-500 to-amber-700 rounded flex items-center justify-center">
                <span className="text-black font-bold text-sm">B</span>
              </div>
              <div>
                <h1 className="text-base font-light text-white" style={{ fontFamily: 'Playfair Display, serif' }}>The Belonging Sessions</h1>
              </div>
              {/* Inline mini stats */}
              <div className="hidden lg:flex items-center gap-4 ml-6 text-xs">
                <span className="text-amber-500 font-medium">{registrations.length} <span className="text-stone-500">reg</span></span>
                <span className="text-stone-400">{registrations.filter(r => r.attending === 'intro').length} <span className="text-stone-500">intro</span></span>
                <span className="text-stone-400">{registrations.filter(r => r.attending === 'full_series').length} <span className="text-stone-500">series</span></span>
                <span className="text-stone-400">{contactSubmissions.length} <span className="text-stone-500">contacts</span></span>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <Button 
                onClick={() => setActiveTab('settings')}
                variant="ghost" 
                size="sm" 
                className="text-stone-400 hover:text-amber-500 hover:bg-amber-500/10 h-7 w-7 p-0"
                title="Settings"
              >
                <Settings className="h-4 w-4" />
              </Button>
              <Button 
                onClick={() => {
                  localStorage.setItem('workbook_user_id', 'admin-preview-' + Date.now());
                  localStorage.setItem('workbook_user_name', localStorage.getItem('admin_username') || 'Admin Preview');
                  localStorage.setItem('workbook_user_email', 'admin@preview.local');
                  window.open('/dashboard', '_blank');
                }}
                variant="ghost" 
                size="sm" 
                className="text-amber-500 hover:bg-amber-500/10 h-7 px-2 text-xs"
                title="Preview as Student"
              >
                <UserPlus className="h-3 w-3 mr-1" />
                <span className="hidden sm:inline">View</span>
              </Button>
              <a href="/belonging-ebook">
                <Button variant="ghost" size="sm" className="text-stone-400 hover:text-amber-500 h-7 px-2 text-xs">
                  📖
                </Button>
              </a>
              <a href="/">
                <Button variant="ghost" size="sm" className="text-stone-400 hover:text-amber-500 h-7 w-7 p-0">
                  🏠
                </Button>
              </a>
              <Button onClick={handleLogout} variant="ghost" size="sm" className="text-red-400/70 hover:text-red-400 h-7 px-2 text-xs">
                Logout
              </Button>
            </div>
          </div>
          
          {/* Compact Tab Navigation */}
          <div className="overflow-x-auto -mx-4 px-4 py-1">
            <TabsList className="inline-flex gap-0 bg-transparent border-b-0 pb-0 min-w-max">
              <TabsTrigger 
                value="registrations" 
                className="relative px-3 py-1.5 text-xs font-medium text-stone-500 data-[state=active]:text-amber-500 data-[state=active]:border-b-2 data-[state=active]:border-amber-500 data-[state=active]:bg-transparent rounded-none bg-transparent hover:text-amber-400 transition-colors"
              >
                👥 Registrations
                {registrations.filter(r => new Date(r.created_at) > new Date(Date.now() - 24*60*60*1000)).length > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-amber-500 rounded-full"></span>
                )}
              </TabsTrigger>
              <TabsTrigger 
                value="seats" 
                className="px-3 py-1.5 text-xs font-medium text-stone-500 data-[state=active]:text-amber-500 data-[state=active]:border-b-2 data-[state=active]:border-amber-500 data-[state=active]:bg-transparent rounded-none bg-transparent hover:text-amber-400 transition-colors"
              >
                💺 Seats
              </TabsTrigger>
              <TabsTrigger 
                value="events" 
                className="px-3 py-1.5 text-xs font-medium text-stone-500 data-[state=active]:text-amber-500 data-[state=active]:border-b-2 data-[state=active]:border-amber-500 data-[state=active]:bg-transparent rounded-none bg-transparent hover:text-amber-400 transition-colors"
              >
                📅 Events
              </TabsTrigger>
              <TabsTrigger 
                value="contacts" 
                className="relative px-3 py-1.5 text-xs font-medium text-stone-500 data-[state=active]:text-amber-500 data-[state=active]:border-b-2 data-[state=active]:border-amber-500 data-[state=active]:bg-transparent rounded-none bg-transparent hover:text-amber-400 transition-colors"
              >
                📝 Contacts
                {contactSubmissions.filter(c => new Date(c.created_at) > new Date(Date.now() - 24*60*60*1000)).length > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-amber-500 rounded-full"></span>
                )}
              </TabsTrigger>
              <TabsTrigger 
                value="messages" 
                className="px-3 py-1.5 text-xs font-medium text-stone-500 data-[state=active]:text-amber-500 data-[state=active]:border-b-2 data-[state=active]:border-amber-500 data-[state=active]:bg-transparent rounded-none bg-transparent hover:text-amber-400 transition-colors"
              >
                💬 Messages
              </TabsTrigger>
              <TabsTrigger 
                value="email" 
                className="px-3 py-1.5 text-xs font-medium text-stone-500 data-[state=active]:text-amber-500 data-[state=active]:border-b-2 data-[state=active]:border-amber-500 data-[state=active]:bg-transparent rounded-none bg-transparent hover:text-amber-400 transition-colors"
              >
                ✉️ Email
              </TabsTrigger>
              <TabsTrigger 
                value="lore" 
                className="px-3 py-1.5 text-xs font-medium text-stone-500 data-[state=active]:text-amber-500 data-[state=active]:border-b-2 data-[state=active]:border-amber-500 data-[state=active]:bg-transparent rounded-none bg-transparent hover:text-amber-400 transition-colors"
              >
                📚 LORE
              </TabsTrigger>
              <TabsTrigger 
                value="ebook" 
                className="px-3 py-1.5 text-xs font-medium text-stone-500 data-[state=active]:text-amber-500 data-[state=active]:border-b-2 data-[state=active]:border-amber-500 data-[state=active]:bg-transparent rounded-none bg-transparent hover:text-amber-400 transition-colors"
              >
                📚 eBook
              </TabsTrigger>
              <TabsTrigger 
                value="announcements" 
                className="px-3 py-1.5 text-xs font-medium text-stone-500 data-[state=active]:text-amber-500 data-[state=active]:border-b-2 data-[state=active]:border-amber-500 data-[state=active]:bg-transparent rounded-none bg-transparent hover:text-amber-400 transition-colors"
              >
                📢 Announce
              </TabsTrigger>
              <TabsTrigger 
                value="blog" 
                className="px-3 py-1.5 text-xs font-medium text-stone-500 data-[state=active]:text-amber-500 data-[state=active]:border-b-2 data-[state=active]:border-amber-500 data-[state=active]:bg-transparent rounded-none bg-transparent hover:text-amber-400 transition-colors"
              >
                📝 Blog
              </TabsTrigger>
              <TabsTrigger 
                value="resources" 
                className="px-3 py-1.5 text-xs font-medium text-stone-500 data-[state=active]:text-amber-500 data-[state=active]:border-b-2 data-[state=active]:border-amber-500 data-[state=active]:bg-transparent rounded-none bg-transparent hover:text-amber-400 transition-colors"
              >
                📁 Resources
              </TabsTrigger>
              <TabsTrigger 
                value="experimental" 
                className="px-3 py-1.5 text-xs font-medium text-stone-500 data-[state=active]:text-amber-500 data-[state=active]:border-b-2 data-[state=active]:border-amber-500 data-[state=active]:bg-transparent rounded-none bg-transparent hover:text-amber-400 transition-colors"
              >
                🔬 Lab
              </TabsTrigger>
              <TabsTrigger 
                value="settings" 
                className="px-3 py-1.5 text-xs font-medium text-stone-500 data-[state=active]:text-amber-500 data-[state=active]:border-b-2 data-[state=active]:border-amber-500 data-[state=active]:bg-transparent rounded-none bg-transparent hover:text-amber-400 transition-colors"
              >
                ⚙️ Settings
              </TabsTrigger>
            </TabsList>
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <section className="py-3 px-4">
        <div className="max-w-7xl mx-auto">
          
          {/* Seats Tab Content */}
          <TabsContent value="seats" className="mt-0">
            <div className="bg-stone-900/80 rounded-xl border border-stone-800 p-6">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-xl font-light text-white" style={{ fontFamily: 'Playfair Display, serif' }}>Seat Availability</h2>
                  <p className="text-sm text-stone-500">Manage capacity for your sessions</p>
                </div>
                <div className="flex gap-3 flex-wrap">
                  <Button 
                    onClick={toggleIntroWaitingList}
                    variant={introWaitingListEnabled ? 'default' : 'outline'}
                    className={introWaitingListEnabled ? 'bg-red-500/80 text-white hover:bg-red-500' : 'border-stone-600 text-stone-300 hover:border-amber-500/50 hover:text-amber-500'}
                    size="sm"
                  >
                    {introWaitingListEnabled ? 'Free Intro: FULL' : 'Free Intro: OPEN'}
                  </Button>
                  <Button 
                    onClick={toggleSeriesWaitingList}
                    variant={seriesWaitingListEnabled ? 'default' : 'outline'}
                    className={seriesWaitingListEnabled ? 'bg-red-500/80 text-white hover:bg-red-500' : 'border-stone-600 text-stone-300 hover:border-amber-500/50 hover:text-amber-500'}
                    size="sm"
                  >
                    {seriesWaitingListEnabled ? 'Full Series: FULL' : 'Full Series: OPEN'}
                  </Button>
                  <Button 
                    onClick={() => setEditingSeats(!editingSeats)}
                    variant="outline"
                    className="border-stone-600 text-stone-300 hover:border-amber-500/50 hover:text-amber-500"
                    size="sm"
                  >
                    {editingSeats ? 'Cancel' : 'Edit Limits'}
                  </Button>
                </div>
              </div>
              
              {editingSeats ? (
                <Card className="bg-stone-800/50 border-stone-700">
                  <CardContent className="p-6 space-y-4">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label className="text-stone-400">Free Intro Max Seats</Label>
                        <Input
                          type="number"
                          value={newSeats.intro_max_seats}
                          onChange={(e) => setNewSeats({...newSeats, intro_max_seats: parseInt(e.target.value)})}
                          className="bg-stone-800 border-stone-700 text-white focus:border-amber-500"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-stone-400">Eight-Part Series Max Seats</Label>
                        <Input
                          type="number"
                          value={newSeats.series_max_seats}
                          onChange={(e) => setNewSeats({...newSeats, series_max_seats: parseInt(e.target.value)})}
                          className="bg-stone-800 border-stone-700 text-white focus:border-amber-500"
                        />
                      </div>
                    </div>
                    <Button 
                      onClick={updateSeats}
                      className="bg-gradient-to-r from-amber-600 to-amber-700 text-black hover:from-amber-500 hover:to-amber-600 font-semibold"
                    >
                      Save Changes
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-gradient-to-br from-stone-800 to-stone-900 rounded-xl p-6 border border-amber-500/30">
                    <h3 className="text-lg font-light text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>Free Introduction</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-stone-400">Filled</span>
                        <span className="text-2xl font-light text-amber-500">{seatInfo.intro_filled} / {seatInfo.intro_max_seats}</span>
                      </div>
                      <div className="h-2 bg-stone-700 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-amber-600 to-amber-500 transition-all"
                          style={{ width: `${seatInfo.intro_percent_filled || 0}%` }}
                        ></div>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-stone-500">{seatInfo.intro_available} available</span>
                        <span className="text-amber-500/70 font-medium">{seatInfo.intro_percent_filled}% full</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-stone-800 to-stone-900 rounded-xl p-6 border border-stone-700">
                    <h3 className="text-lg font-light text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>Eight-Part Series</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-stone-400">Filled</span>
                        <span className="text-2xl font-light text-stone-300">{seatInfo.series_filled} / {seatInfo.series_max_seats}</span>
                      </div>
                      <div className="h-2 bg-stone-700 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-stone-500 to-stone-400 transition-all"
                          style={{ width: `${seatInfo.series_percent_filled || 0}%` }}
                        ></div>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-stone-500">{seatInfo.series_available} available</span>
                        <span className="text-stone-400 font-medium">{seatInfo.series_percent_filled}% full</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Events Tab Content */}
          <TabsContent value="events" className="mt-0">
            <div className="bg-stone-900/80 rounded-xl border border-stone-800 p-6">
              <EventManager registrations={registrations} />
            </div>
          </TabsContent>



          {/* Registrations Tab */}
          <TabsContent value="registrations">
              <div>
                <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-2xl font-light text-white" style={{ fontFamily: 'Playfair Display, serif' }}>Registrations ({filteredRegistrations.length})</h2>
                  <p className="text-sm text-stone-500">
                    Total: {registrations.length} | 
                    Newsletter Only: {registrations.filter(r => r.attending === 'newsletter').length}
                  </p>
                </div>
                <div className="flex gap-3">
                  <Button
                    onClick={() => setShowNewsletterOnly(!showNewsletterOnly)}
                    variant={showNewsletterOnly ? "default" : "outline"}
                    className={showNewsletterOnly ? "bg-amber-600 text-black hover:bg-amber-500" : "border-stone-600 text-stone-300 hover:border-amber-500/50"}
                    style={{ borderRadius: 0 }}
                  >
                    {showNewsletterOnly ? '📧 Showing All' : '🚫 Hiding Newsletter'}
                  </Button>
                  <Button 
                    onClick={async () => {
                      if (window.confirm('Create accounts for all registrations without accounts? They will receive email with password: "password"')) {
                        try {
                          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/create-all-accounts`, {
                            method: 'POST',
                            headers: { 'Authorization': `Bearer ${token}` }
                          });
                          if (response.ok) {
                            const data = await response.json();
                            toast.success(`Created ${data.created} accounts!`);
                          }
                        } catch (error) {
                          toast.error('Failed to create accounts');
                        }
                      }
                    }}
                    className="bg-amber-600/80 text-black hover:bg-amber-500"
                    style={{ borderRadius: 0 }}
                  >
                    👥 Create All Accounts
                  </Button>
                  {selectedUsers.length > 0 && (
                    <Button 
                      onClick={emailSelected}
                      className="bg-stone-700 text-white hover:bg-stone-600"
                      style={{ borderRadius: 0 }}
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      Email Selected ({selectedUsers.length})
                    </Button>
                  )}
                  <Dialog open={addUserDialogOpen} onOpenChange={setAddUserDialogOpen}>
                    <DialogTrigger asChild>
                      <Button 
                        className="bg-gradient-to-r from-amber-600 to-amber-700 text-black hover:from-amber-500 hover:to-amber-600 font-semibold"
                        style={{ borderRadius: 0 }}
                      >
                        <UserPlus className="w-4 h-4 mr-2" />
                        Add New User
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-xl max-h-[80vh] overflow-y-auto bg-stone-900 border border-stone-700 text-white" style={{ borderRadius: 0 }}>
                      <DialogHeader>
                        <DialogTitle className="text-2xl font-light text-white" style={{ fontFamily: 'Playfair Display, serif' }}>Add New User</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleAddUser} className="space-y-6 mt-6">
                        <div className="grid md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label className="text-stone-400">First Name *</Label>
                            <Input
                              required
                              value={newUserData.first_name}
                              onChange={(e) => setNewUserData(prev => ({ ...prev, first_name: e.target.value }))}
                              className="bg-stone-800 border-stone-700 text-white focus:border-amber-500"
                              style={{ borderRadius: 0 }}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-stone-400">Last Name *</Label>
                            <Input
                              required
                              value={newUserData.last_name}
                              onChange={(e) => setNewUserData(prev => ({ ...prev, last_name: e.target.value }))}
                              className="bg-stone-800 border-stone-700 text-white focus:border-amber-500"
                              style={{ borderRadius: 0 }}
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-stone-400">Email *</Label>
                          <Input
                            type="email"
                            required
                            value={newUserData.email}
                            onChange={(e) => setNewUserData(prev => ({ ...prev, email: e.target.value }))}
                            className="bg-stone-800 border-stone-700 text-white focus:border-amber-500"
                            style={{ borderRadius: 0 }}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-stone-400">Phone</Label>
                          <Input
                            type="tel"
                            value={newUserData.phone}
                            onChange={(e) => setNewUserData(prev => ({ ...prev, phone: e.target.value }))}
                            className="bg-stone-800 border-stone-700 text-white focus:border-amber-500"
                            style={{ borderRadius: 0 }}
                          />
                        </div>
                        <div className="space-y-3">
                          <Label>Attending:</Label>
                          <RadioGroup value={newUserData.attending} onValueChange={(value) => setNewUserData(prev => ({ ...prev, attending: value }))}>
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="intro" id="add-intro" />
                              <Label htmlFor="add-intro">Free Introduction</Label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="full_series" id="add-series" />
                              <Label htmlFor="add-series">Eight-Part Series</Label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="both" id="add-both" />
                              <Label htmlFor="add-both">Both</Label>
                            </div>
                          </RadioGroup>
                        </div>
                        <div className="space-y-3">
                          <Label>Price:</Label>
                          <RadioGroup value={newUserData.price_selected} onValueChange={(value) => setNewUserData(prev => ({ ...prev, price_selected: value }))}>
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="free" id="add-free" />
                              <Label htmlFor="add-free">Free (Intro Only)</Label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="175" id="add-175" />
                              <Label htmlFor="add-175">$175</Label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="scholarship" id="add-scholarship" />
                              <Label htmlFor="add-scholarship">Scholarship</Label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="pay_forward" id="add-pay-forward" />
                              <Label htmlFor="add-pay-forward">Pay Forward</Label>
                            </div>
                          </RadioGroup>
                        </div>
                        <div className="space-y-3">
                          <Label>Payment Status:</Label>
                          <RadioGroup value={newUserData.payment_status} onValueChange={(value) => setNewUserData(prev => ({ ...prev, payment_status: value }))}>
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="pending" id="add-pending" />
                              <Label htmlFor="add-pending">Pending</Label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="paid" id="add-paid" />
                              <Label htmlFor="add-paid">Paid</Label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="free" id="add-status-free" />
                              <Label htmlFor="add-status-free">Free</Label>
                            </div>
                          </RadioGroup>
                        </div>
                        <div className="space-y-2">
                          <Label>Payment Method</Label>
                          <select
                            value={newUserData.payment_method}
                            onChange={(e) => setNewUserData(prev => ({ ...prev, payment_method: e.target.value }))}
                            className="w-full p-3 border border-stone-700 bg-stone-800 text-white"
                            style={{ borderRadius: 0 }}
                          >
                            <option value="">Not Set</option>
                            <option value="paypal">PayPal</option>
                            <option value="cash">Cash</option>
                            <option value="check">Check</option>
                            <option value="credit_card">Credit Card</option>
                            <option value="free">Free (Intro)</option>
                          </select>
                        </div>
                        <div className="space-y-2">
                          <Label>Referral</Label>
                          <Input
                            value={newUserData.referral}
                            onChange={(e) => setNewUserData(prev => ({ ...prev, referral: e.target.value }))}
                            className="bg-stone-800 border-stone-700 text-white focus:border-amber-500"
                            style={{ borderRadius: 0 }}
                          />
                        </div>
                        <div className="flex items-center space-x-3 p-4 bg-stone-800/50 rounded">
                          <input
                            type="checkbox"
                            id="add-in-person"
                            checked={newUserData.in_person}
                            onChange={(e) => setNewUserData(prev => ({ ...prev, in_person: e.target.checked }))}
                            className="cursor-pointer"
                          />
                          <Label htmlFor="add-in-person" className="font-normal cursor-pointer">
                            Attending IN-PERSON at Defiance Springs
                          </Label>
                        </div>
                        <Button 
                          type="submit" 
                          className="w-full bg-amber-600 text-black hover:bg-black/90 py-6 text-lg"
                          style={{ borderRadius: 0 }}
                        >
                          Add User
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                  <Button 
                    onClick={fetchRegistrations} 
                    variant="outline"
                    className="bg-stone-800 border-stone-700 text-white focus:border-amber-500"
                    style={{ borderRadius: 0 }}
                    disabled={loading}
                  >
                    {loading ? 'Loading...' : 'Refresh'}
                  </Button>
                </div>
              </div>

          <div className="bg-stone-900/50 border border-stone-800 rounded-lg overflow-x-auto">
            <Table className="bg-stone-900" style={{ backgroundColor: '#1c1917' }}>
              <TableHeader className="bg-stone-800/80 border-b border-stone-700" style={{ backgroundColor: '#292524' }}>
                <TableRow className="hover:bg-stone-800 border-b border-stone-700" style={{ backgroundColor: 'transparent' }}>
                  <TableHead className="font-bold w-12 text-amber-500">
                    <input
                      type="checkbox"
                      checked={selectedUsers.length === registrations.length && registrations.length > 0}
                      onChange={selectAllUsers}
                      className="cursor-pointer"
                    />
                  </TableHead>
                  <TableHead className="font-bold text-amber-500/80">Name</TableHead>
                  <TableHead className="font-bold text-amber-500/80">Email</TableHead>
                  <TableHead className="font-bold text-amber-500/80">Phone</TableHead>
                  <TableHead className="font-bold text-amber-500/80">Session</TableHead>
                  <TableHead className="font-bold text-amber-500/80">In-Person</TableHead>
                  <TableHead className="font-bold text-amber-500/80">Price</TableHead>
                  <TableHead className="font-bold text-amber-500/80">Payment Status</TableHead>
                  <TableHead className="font-bold text-amber-500/80">Payment Method</TableHead>
                  <TableHead className="font-bold text-amber-500/80">Date</TableHead>
                  <TableHead className="font-bold text-amber-500/80">Account Status</TableHead>
                  <TableHead className="font-bold text-amber-500/80">General Notes</TableHead>
                  <TableHead className="font-bold text-amber-500/80">Scholarship Notes</TableHead>
                  <TableHead className="font-bold text-amber-500/80">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody className="bg-stone-900/50" style={{ backgroundColor: '#1c1917' }}>
                {filteredRegistrations.length === 0 ? (
                  <TableRow className="border-b border-stone-700" style={{ backgroundColor: '#1c1917' }}>
                    <TableCell colSpan={11} className="text-center py-12 text-stone-500">
                      No registrations yet
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredRegistrations.map((reg) => (
                    <TableRow 
                      key={reg.id}
                      className="hover:bg-stone-800/50 transition-colors border-b border-stone-700/50 text-stone-300"
                      style={{ backgroundColor: '#1c1917' }}
                    >
                      {editingReg === reg.id ? (
                        // EDITING MODE - Show input fields
                        <>
                          <TableCell>
                            <input
                              type="checkbox"
                              checked={selectedUsers.includes(reg.email)}
                              onChange={() => toggleUserSelection(reg.email)}
                              className="cursor-pointer"
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              value={editForm.full_name || ''}
                              onChange={(e) => setEditForm({...editForm, full_name: e.target.value})}
                              className="bg-stone-800 border-stone-700 text-white focus:border-amber-500"
                              style={{ borderRadius: 0 }}
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              type="email"
                              value={editForm.email || ''}
                              onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                              className="bg-stone-800 border-stone-700 text-white focus:border-amber-500"
                              style={{ borderRadius: 0 }}
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              value={editForm.phone || ''}
                              onChange={(e) => setEditForm({...editForm, phone: e.target.value})}
                              className="bg-stone-800 border-stone-700 text-white focus:border-amber-500"
                              style={{ borderRadius: 0 }}
                            />
                          </TableCell>
                          <TableCell>
                            <select
                              value={editForm.attending || 'intro'}
                              onChange={(e) => setEditForm({...editForm, attending: e.target.value})}
                              className="w-full p-2 border border-stone-700 bg-stone-800 text-white"
                              style={{ borderRadius: 0 }}
                            >
                              <option value="intro">Free Intro</option>
                              <option value="full_series">Full Series</option>
                              <option value="both">Both</option>
                              <option value="newsletter">Newsletter Only</option>
                              <option value="other">Other</option>
                              <option value="both">Both</option>
                            </select>
                          </TableCell>
                          <TableCell>
                            <input
                              type="checkbox"
                              checked={editForm.in_person || false}
                              onChange={(e) => setEditForm({...editForm, in_person: e.target.checked})}
                              className="cursor-pointer"
                            />
                          </TableCell>
                          <TableCell>
                            <select
                              value={editForm.price_selected || '175'}
                              onChange={(e) => setEditForm({...editForm, price_selected: e.target.value})}
                              className="w-full p-2 border border-stone-700 bg-stone-800 text-white"
                              style={{ borderRadius: 0 }}
                            >
                              <option value="free">Free (Intro Only)</option>
                              <option value="175">$175</option>
                              <option value="scholarship">Scholarship</option>
                              <option value="pay_forward">Pay It Forward</option>
                              <option value="newsletter">Newsletter Only</option>
                              <option value="other">Other</option>
                              <option value="pay_forward">Pay Forward</option>
                            </select>
                          </TableCell>
                          <TableCell>
                            <select
                              value={editForm.payment_status || 'pending'}
                              onChange={(e) => setEditForm({...editForm, payment_status: e.target.value})}
                              className="w-full p-2 border border-stone-700 bg-stone-800 text-white"
                              style={{ borderRadius: 0 }}
                            >
                              <option value="pending">Pending</option>
                              <option value="paid">Paid</option>
                              <option value="free">Free</option>
                            </select>
                          </TableCell>
                          <TableCell>
                            <select
                              value={editForm.payment_method || ''}
                              onChange={(e) => setEditForm({...editForm, payment_method: e.target.value})}
                              className="w-full p-2 border border-stone-700 bg-stone-800 text-white"
                              style={{ borderRadius: 0 }}
                            >
                              <option value="">Not Set</option>
                              <option value="paypal">PayPal</option>
                              <option value="cash">Cash</option>
                              <option value="check">Check</option>
                              <option value="credit_card">Credit Card</option>
                              <option value="free">Free (Intro)</option>
                            </select>
                          </TableCell>
                          <TableCell>{new Date(reg.created_at).toLocaleDateString()}</TableCell>
                          <TableCell>
                            {reg.account_created ? (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                                ✓ Activated
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-gray-100 text-stone-400">
                                ○ Not Activated
                              </span>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => saveEdit(reg.id)}
                                className="text-green-600 hover:text-green-800"
                              >
                                Save
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setEditingReg(null)}
                                className="text-stone-400 hover:text-gray-800"
                              >
                                Cancel
                              </Button>
                            </div>
                          </TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell>
                            <input
                              type="checkbox"
                              checked={selectedUsers.includes(reg.email)}
                              onChange={() => toggleUserSelection(reg.email)}
                              className="cursor-pointer"
                            />
                          </TableCell>
                          <TableCell 
                            className="font-medium cursor-pointer hover:text-blue-600"
                            onClick={() => navigate(`/admin/user/${reg.id}`)}
                          >
                            {reg.full_name}
                          </TableCell>
                          <TableCell 
                            className="cursor-pointer hover:text-blue-600"
                            onClick={() => navigate(`/admin/user/${reg.id}`)}
                          >
                            {reg.email}
                          </TableCell>
                          <TableCell>{reg.phone || '—'}</TableCell>
                          <TableCell>
                            <span className={`px-2 py-1 text-xs font-semibold ${
                              (reg.attending || 'intro') === 'intro' ? 'bg-blue-100 text-blue-800' : 
                              (reg.attending || 'intro') === 'both' ? 'bg-purple-100 text-purple-800' :
                              'bg-green-100 text-green-800'
                            }`}>
                              {(reg.attending || 'intro') === 'intro' ? 'Free Intro' : 
                               (reg.attending || 'intro') === 'both' ? 'Both' : 'Full Series'}
                            </span>
                          </TableCell>
                          <TableCell>
                            {reg.in_person ? '✓' : '—'}
                          </TableCell>
                          <TableCell>
                            {reg.price_selected === 'scholarship' ? (
                              <span className="px-2 py-1 text-xs font-semibold bg-amber-500/20 text-amber-400">
                                Scholarship
                              </span>
                            ) : reg.price_selected === 'pay_forward' ? (
                              <span className="px-2 py-1 text-xs font-semibold bg-blue-100 text-blue-800">
                                Pay Forward
                              </span>
                            ) : reg.price_selected === 'free' ? (
                              <span className="px-2 py-1 text-xs font-semibold bg-gray-100 text-gray-800">
                                Free
                              </span>
                            ) : (
                              `$${reg.price_selected}`
                            )}
                          </TableCell>
                          <TableCell>
                            <span className={`px-2 py-1 text-xs font-semibold ${
                              (reg.payment_status || 'pending') === 'paid' ? 'bg-green-100 text-green-800' :
                              (reg.payment_status || 'pending') === 'free' ? 'bg-gray-100 text-gray-800' :
                              'bg-orange-100 text-orange-800'
                            }`}>
                              {(reg.payment_status || 'pending').charAt(0).toUpperCase() + (reg.payment_status || 'pending').slice(1)}
                            </span>
                          </TableCell>
                          <TableCell className="capitalize">
                            {reg.payment_method ? reg.payment_method.replace('_', ' ') : '—'}
                          </TableCell>
                          <TableCell>{new Date(reg.created_at).toLocaleDateString()}</TableCell>
                          <TableCell>
                            {reg.account_created ? (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                                ✓ Activated
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-gray-100 text-stone-400">
                                ○ Not Activated
                              </span>
                            )}
                            {reg.attending === 'newsletter' && (
                              <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                                📧 Newsletter
                              </span>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => startEditing(reg)}
                                className="text-blue-600 hover:text-blue-800"
                              >
                                Edit
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={async () => {
                                  if (window.confirm(`Create login account for ${reg.full_name}? They'll receive email with password: "password"`)) {
                                    try {
                                      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/create-user-account/${reg.id}`, {
                                        method: 'POST',
                                        headers: { 'Authorization': `Bearer ${token}` }
                                      });
                                      if (response.ok) {
                                        const data = await response.json();
                                        toast.success(`Account created for ${reg.full_name}!`);
                                      } else {
                                        const errorData = await response.text();
                                        console.error('Create account failed:', errorData);
                                        toast.error(`Failed to create account: ${response.status}`);
                                      }
                                    } catch (error) {
                                      console.error('Create account error:', error);
                                      toast.error(`Error creating account: ${error.message}`);
                                    }
                                  }
                                }}
                                className="text-green-600 hover:text-green-800"
                                title="Create login account"
                              >
                                👤 Account
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(reg.id)}
                                className="text-red-600 hover:text-red-800"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
            </div>
            </TabsContent>

            {/* Contact Submissions Tab */}
            <TabsContent value="contacts">
          <h3 className="text-2xl font-bold mb-6">Contact Form Submissions & Waiting List</h3>
          {contactSubmissions.length === 0 ? (
            <p className="text-stone-400">No contact submissions yet.</p>
          ) : (
            <div className="space-y-4">
              {contactSubmissions.map((contact) => (
                <Card key={contact.id} style={{ borderRadius: 0 }} className={
                  contact.type === 'waiting_list' ? 'border-2 border-yellow-400' : 
                  contact.type === 'scholarship_request' ? 'border-2 border-amber-500' : ''
                }>
                  <CardContent className="p-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <p className="font-bold text-lg">{contact.name}</p>
                          {contact.type === 'waiting_list' && (
                            <span className="px-3 py-1 text-xs font-semibold bg-amber-500/20 text-amber-400 rounded-full">
                              WAITING LIST
                            </span>
                          )}
                          {contact.type === 'scholarship_request' && (
                            <span className="px-3 py-1 text-xs font-semibold bg-amber-100 text-amber-800 rounded-full">
                              SCHOLARSHIP REQUEST
                            </span>
                          )}
                        </div>
                        <p className="text-stone-400 mb-1"><strong>Email:</strong> {contact.email}</p>
                        {contact.phone && <p className="text-stone-400 mb-1"><strong>Phone:</strong> {contact.phone}</p>}
                        <p className="text-xs text-stone-500 mt-2">Submitted: {new Date(contact.created_at).toLocaleString()}</p>
                      </div>
                      <div className="space-y-2">
                        <p className="text-sm"><strong>Type:</strong> <span className="capitalize">{contact.type?.replace('_', ' ')}</span></p>
                        {contact.interest && <p className="text-sm"><strong>Interest:</strong> {contact.interest}</p>}
                        {contact.contactPreference && <p className="text-sm"><strong>Contact Preference:</strong> {contact.contactPreference}</p>}
                      </div>
                    </div>
                    {contact.comments && (
                      <div className="mt-4 p-4 bg-stone-800/50 border-l-4 border-amber-500">
                        <p className="text-sm font-semibold mb-2">Comments:</p>
                        <p className="text-sm text-gray-700">{contact.comments}</p>
                      </div>
                    )}
                  </CardContent>
                  <div className="border-t p-4 flex justify-end gap-2">
                    {contact.newsletter_added ? (
                      <span className="px-3 py-2 text-sm font-semibold bg-green-100 text-green-800 rounded flex items-center">
                        <Mail className="w-4 h-4 mr-1" /> Added to Newsletter ✓
                      </span>
                    ) : (
                      <Button
                        onClick={async () => {
                          try {
                            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/add-to-newsletter`, {
                              method: 'POST',
                              headers: { 
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${token}` 
                              },
                              body: JSON.stringify({
                                contact_id: contact.id,
                                email: contact.email,
                                name: contact.name
                              })
                            });
                            const responseText = await response.text();
                            let data;
                            try {
                              data = JSON.parse(responseText);
                            } catch {
                              throw new Error('Server error');
                            }
                            if (!response.ok) {
                              throw new Error(data.detail || 'Failed to add to newsletter');
                            }
                            toast.success(`Added ${contact.email} to newsletter list!`);
                            fetchContactSubmissions();
                            fetchRegistrations();
                          } catch (error) {
                            toast.error(error.message);
                          }
                        }}
                        variant="outline"
                        className="text-green-600 hover:bg-green-50 border-green-300"
                        style={{ borderRadius: 0 }}
                        data-testid={`add-newsletter-${contact.id}`}
                      >
                        <Mail className="w-4 h-4 mr-1" /> Add to Newsletter
                      </Button>
                    )}
                    <Button
                      onClick={async () => {
                        if (window.confirm(`Delete contact from ${contact.name}?`)) {
                          try {
                            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/contact/${contact.id}`, {
                              method: 'DELETE',
                              headers: { 'Authorization': `Bearer ${token}` }
                            });
                            if (response.ok) {
                              toast.success('Contact deleted');
                              fetchContactSubmissions();
                            }
                          } catch (error) {
                            toast.error('Failed to delete');
                          }
                        }
                      }}
                      variant="outline"
                      className="text-red-600 hover:bg-red-50"
                      style={{ borderRadius: 0 }}
                    >
                      <Trash2 className="w-4 h-4 mr-1" /> Delete
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Messages Tab */}
        <TabsContent value="messages">
          <div>
            <h3 className="text-2xl font-bold mb-6">Messages</h3>
            <p className="text-stone-400">Message management coming soon...</p>
          </div>
        </TabsContent>

        {/* Send Email Tab */}
        <TabsContent value="email">
          <h3 className="text-2xl font-bold mb-6">Send Email to Users</h3>
          <Card style={{ borderRadius: 0 }}>
            <CardContent className="p-6">
              <form onSubmit={handleSendEmail} className="space-y-6">
                <div className="space-y-2">
                  <Label>To (email addresses - one per line or comma-separated)</Label>
                  <Textarea
                    required
                    value={emailData.to_emails}
                    onChange={(e) => setEmailData({...emailData, to_emails: e.target.value})}
                    placeholder="user@example.com&#10;another@example.com&#10;or: email1@test.com, email2@test.com"
                    className="border-black min-h-[100px]"
                    style={{ borderRadius: 0 }}
                  />
                  <p className="text-sm text-stone-400">Tip: You can copy emails from the registrations table</p>
                </div>
                
                <div className="space-y-2">
                  <Label>Subject</Label>
                  <Input
                    required
                    value={emailData.subject}
                    onChange={(e) => setEmailData({...emailData, subject: e.target.value})}
                    placeholder="Email subject line"
                    className="bg-stone-800 border-stone-700 text-white focus:border-amber-500"
                    style={{ borderRadius: 0 }}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Message</Label>
                  <Textarea
                    required
                    value={emailData.message}
                    onChange={(e) => setEmailData({...emailData, message: e.target.value})}
                    placeholder="Type your message here..."
                    className="border-black min-h-[300px]"
                    style={{ borderRadius: 0 }}
                  />
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="html-email"
                      checked={emailData.is_html}
                      onChange={(e) => setEmailData({...emailData, is_html: e.target.checked})}
                    />
                    <Label htmlFor="html-email" className="font-normal">
                      Message contains HTML (for advanced formatting)
                    </Label>
                  </div>
                </div>
                
                <div className="bg-blue-50 border-l-4 border-blue-500 p-4">
                  <p className="text-sm text-blue-800">
                    <strong>Note:</strong> All emails will be sent from hello@defiancesprings.com with your custom message.
                  </p>
                </div>
                
                <Button 
                  type="submit" 
                  className="bg-amber-600 text-black hover:bg-black/90 px-8 py-4 text-lg"
                  disabled={sendingEmail}
                  style={{ borderRadius: 0 }}
                >
                  <Mail className="w-4 h-4 mr-2" />
                  {sendingEmail ? 'Sending...' : 'Send Email'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Feedback Tab */}
        <TabsContent value="feedback">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-2xl font-bold">User Feedback & Testimonials</h3>
            <Button 
              onClick={fetchFeedback}
              variant="outline"
              className="bg-stone-800 border-stone-700 text-white focus:border-amber-500"
              style={{ borderRadius: 0 }}
              disabled={loadingFeedback}
            >
              {loadingFeedback ? 'Loading...' : 'Refresh'}
            </Button>
          </div>
          
          {feedbackList.length === 0 ? (
            <p className="text-stone-400">No feedback submitted yet.</p>
          ) : (
            <div className="space-y-4">
              {feedbackList.map((feedback) => (
                <Card key={feedback.id} style={{ borderRadius: 0 }} className={feedback.willing_testimonial ? 'border-2 border-green-500' : ''}>
                  <CardContent className="p-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <p className="font-bold text-lg">{feedback.user_name}</p>
                        <p className="text-stone-400">{feedback.user_email}</p>
                        <p className="text-sm text-stone-500">
                          {feedback.course_attended} • {new Date(feedback.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="space-y-2">
                        <h4 className="font-semibold">Ratings:</h4>
                        {Object.entries(feedback.ratings || {}).map(([key, value]) => (
                          value > 0 && (
                            <div key={key} className="flex justify-between text-sm">
                              <span className="capitalize">{key.replace('_', ' ')}</span>
                              <span className="font-bold">{value}/5</span>
                            </div>
                          )
                        ))}
                      </div>
                    </div>
                    
                    {feedback.message && (
                      <div className="mt-4 p-4 bg-stone-800/50 border-l-4 border-amber-500">
                        <h4 className="font-semibold mb-2">Comments:</h4>
                        <p className="text-gray-700">{feedback.message}</p>
                      </div>
                    )}
                    
                    {feedback.willing_testimonial && feedback.testimonial_text && (
                      <div className="mt-4 p-4 bg-green-50 border-l-4 border-green-600">
                        <h4 className="font-semibold mb-2 text-green-800">✨ Testimonial (Ready to Use):</h4>
                        <p className="text-gray-700 italic">"{feedback.testimonial_text}"</p>
                        <p className="text-sm text-stone-400 mt-2">— {feedback.testimonial_name}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Email Logs Tab */}
        <TabsContent value="logs">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-2xl font-bold">Email Activity Logs</h3>
            <div className="flex gap-3">
              <div className="flex gap-2">
                <Button
                  onClick={() => setEmailLogFilter('all')}
                  variant={emailLogFilter === 'all' ? 'default' : 'outline'}
                  className={emailLogFilter === 'all' ? 'bg-amber-600 text-black' : 'border-black'}
                  style={{ borderRadius: 0 }}
                >
                  All
                </Button>
                <Button
                  onClick={() => setEmailLogFilter('success')}
                  variant={emailLogFilter === 'success' ? 'default' : 'outline'}
                  className={emailLogFilter === 'success' ? 'bg-green-600 text-white' : 'border-black'}
                  style={{ borderRadius: 0 }}
                >
                  Sent Mail
                </Button>
                <Button
                  onClick={() => setEmailLogFilter('failed')}
                  variant={emailLogFilter === 'failed' ? 'default' : 'outline'}
                  className={emailLogFilter === 'failed' ? 'bg-red-600 text-white' : 'border-black'}
                  style={{ borderRadius: 0 }}
                >
                  Failed
                </Button>
              </div>
              <Button 
                onClick={fetchEmailLogs}
                variant="outline"
                className="bg-stone-800 border-stone-700 text-white focus:border-amber-500"
                style={{ borderRadius: 0 }}
                disabled={loadingLogs}
              >
                {loadingLogs ? 'Loading...' : 'Refresh'}
              </Button>
            </div>
          </div>
          
          {emailLogs.filter(log => emailLogFilter === 'all' || log.status === emailLogFilter).length === 0 ? (
            <p className="text-stone-400">
              {emailLogFilter === 'success' ? 'No sent emails yet.' :
               emailLogFilter === 'failed' ? 'No failed emails (good news!)' :
               'No email logs yet. Logs will appear after emails are sent.'}
            </p>
          ) : (
            <div className="space-y-3">
              {emailLogs.filter(log => emailLogFilter === 'all' || log.status === emailLogFilter).map((log) => (
                <Card 
                  key={log.id} 
                  style={{ borderRadius: 0 }}
                  className={log.status === 'failed' ? 'border-2 border-red-500' : log.status === 'success' ? 'border-l-4 border-green-500' : ''}
                >
                  <CardContent className="p-4">
                    <div className="grid md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-sm text-stone-400">To</p>
                        <p className="font-semibold">{log.to_email}</p>
                      </div>
                      <div>
                        <p className="text-sm text-stone-400">Subject</p>
                        <p className="font-semibold">{log.subject || '(No subject)'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-stone-400">Status</p>
                        <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                          log.status === 'success' ? 'bg-green-100 text-green-800' :
                          log.status === 'failed' ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {log.status.toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <p className="text-sm text-stone-400">Time</p>
                        <p className="text-sm">{new Date(log.timestamp).toLocaleString()}</p>
                      </div>
                    </div>
                    {log.error && (
                      <div className="mt-3 p-3 bg-red-50 border-l-4 border-red-500">
                        <p className="text-sm font-semibold text-red-800">Error:</p>
                        <p className="text-sm text-red-700">{log.error}</p>
                        {log.error_type && <p className="text-xs text-red-600 mt-1">Type: {log.error_type}</p>}
                      </div>
                    )}
                    {log.html_preview && log.status === 'success' && (
                      <div className="mt-3">
                        <details className="cursor-pointer">
                          <summary className="text-sm font-semibold text-gray-700 hover:text-black">View Email Content (Plain Text)</summary>
                          <div className="mt-2 p-3 bg-stone-800/50 border border-gray-200 rounded">
                            <pre className="text-xs text-gray-800 whitespace-pre-wrap font-mono">
                              {log.html_preview.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim()}
                            </pre>
                          </div>
                        </details>
                      </div>
                    )}
                    {log.smtp_host && (
                      <div className="mt-2">
                        <p className="text-xs text-stone-500">SMTP Host: {log.smtp_host}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings">
          <h3 className="text-2xl font-bold mb-6">Settings & Configuration</h3>
          
          <div className="space-y-8">
            
            {/* Interactive Exercise Manager */}
            <Card style={{ borderRadius: 0 }} className="border-2 border-blue-500">
              <CardHeader>
                <CardTitle className="text-xl">🎯 Interactive Exercise Manager</CardTitle>
                <p className="text-sm text-stone-400">Create exercises for pages 89, 90, 91-92 with AI formatting</p>
              </CardHeader>
              <CardContent>
                <ExerciseManager token={token} />
              </CardContent>
            </Card>

            {/* Homework Assignment Manager */}
            <Card style={{ borderRadius: 0 }} className="border-2 border-orange-500">
              <CardHeader>
                <CardTitle className="text-xl">📝 Homework Assignment Manager</CardTitle>
                <p className="text-sm text-stone-400">Add PDF homework content for each chapter</p>
              </CardHeader>
              <CardContent>
                <HomeworkManager token={token} />
              </CardContent>
            </Card>

            {/* Auto-Response Templates */}
            <Card style={{ borderRadius: 0 }} className="border-2 border-green-500">
              <CardHeader>
                <CardTitle className="text-xl">📧 Auto-Response Templates</CardTitle>
                <p className="text-sm text-stone-400">Automatically respond to contact form submissions</p>
              </CardHeader>
              <CardContent className="space-y-6">
                
                <div className="grid md:grid-cols-3 gap-4">
                  <Card style={{ borderRadius: 0 }} className="border border-blue-400">
                    <CardHeader>
                      <CardTitle className="text-base">Mailing List Response</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        <Input
                          placeholder="Welcome to The Belonging Express!"
                          className="border-blue-500 text-sm"
                          style={{ borderRadius: 0 }}
                        />
                        <Textarea
                          placeholder="Thank you for joining The Belonging Express! We'll keep you updated..."
                          className="border-blue-500 text-sm min-h-[80px]"
                          style={{ borderRadius: 0 }}
                        />
                        <Button className="w-full bg-stone-700 text-white hover:bg-stone-600 text-sm" style={{ borderRadius: 0 }}>
                          Save Mailing List Response
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card style={{ borderRadius: 0 }} className="border border-green-400">
                    <CardHeader>
                      <CardTitle className="text-base">Contact Form Response</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        <Input
                          placeholder="Thank you for reaching out!"
                          className="border-green-500 text-sm"
                          style={{ borderRadius: 0 }}
                        />
                        <Textarea
                          placeholder="Dear {{name}}, Thank you for your message. We've received your inquiry about..."
                          className="border-green-500 text-sm min-h-[80px]"
                          style={{ borderRadius: 0 }}
                        />
                        <Button className="w-full bg-amber-600 text-black hover:bg-amber-500 text-sm" style={{ borderRadius: 0 }}>
                          Save Contact Response
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card style={{ borderRadius: 0 }} className="border border-purple-400">
                    <CardHeader>
                      <CardTitle className="text-base">OFNR Trainer Response</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        <Input
                          placeholder="Thanks for trying our OFNR Trainer!"
                          className="border-purple-500 text-sm"
                          style={{ borderRadius: 0 }}
                        />
                        <Textarea
                          placeholder="We're glad you're exploring compassionate communication..."
                          className="border-purple-500 text-sm min-h-[80px]"
                          style={{ borderRadius: 0 }}
                        />
                        <Button className="w-full bg-purple-600 text-white hover:bg-purple-700 text-sm" style={{ borderRadius: 0 }}>
                          Save Trainer Response
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                <div className="bg-blue-50 border-l-4 border-blue-500 p-4">
                  <h4 className="font-bold text-blue-800 mb-2">Available Placeholders:</h4>
                  <div className="text-sm text-blue-700 space-y-1">
                    <p>• <code>{'{{name}}'}</code> - Contact's name</p>
                    <p>• <code>{'{{email}}'}</code> - Contact's email</p>
                    <p>• <code>{'{{comments}}'}</code> - Their message/comments</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            </div>

            {/* Email System Configuration */}
            <h3 className="text-2xl font-bold mt-12 mb-6">Email System Configuration</h3>
          
          <div className="space-y-8">
            {/* SMTP Settings */}
            <Card style={{ borderRadius: 0 }} className="border-2 border-blue-500">
              <CardHeader>
                <CardTitle className="text-xl">SMTP Email Server Settings</CardTitle>
                <p className="text-sm text-stone-400">Configure email sending credentials (stored securely in database)</p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>SMTP Host</Label>
                    <Input
                      value={smtpSettings.smtp_host}
                      onChange={(e) => setSmtpSettings({...smtpSettings, smtp_host: e.target.value})}
                      placeholder="s2936.can1.stableserver.net"
                      className="bg-stone-800 border-stone-700 text-white focus:border-amber-500"
                      style={{ borderRadius: 0 }}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>SMTP Port</Label>
                    <Input
                      type="number"
                      value={smtpSettings.smtp_port}
                      onChange={(e) => setSmtpSettings({...smtpSettings, smtp_port: parseInt(e.target.value)})}
                      placeholder="465"
                      className="bg-stone-800 border-stone-700 text-white focus:border-amber-500"
                      style={{ borderRadius: 0 }}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>SMTP Username</Label>
                  <Input
                    value={smtpSettings.smtp_username}
                    onChange={(e) => setSmtpSettings({...smtpSettings, smtp_username: e.target.value})}
                    placeholder="Tony@drinkaronia.com"
                    className="bg-stone-800 border-stone-700 text-white focus:border-amber-500"
                    style={{ borderRadius: 0 }}
                  />
                </div>
                <div className="space-y-2">
                  <Label>SMTP Password</Label>
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={smtpSettings.smtp_password}
                    onChange={(e) => setSmtpSettings({...smtpSettings, smtp_password: e.target.value})}
                    placeholder="Enter password to update"
                    className="bg-stone-800 border-stone-700 text-white focus:border-amber-500"
                    style={{ borderRadius: 0 }}
                  />
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="show-password"
                      checked={showPassword}
                      onChange={(e) => setShowPassword(e.target.checked)}
                    />
                    <Label htmlFor="show-password" className="font-normal text-sm">Show password</Label>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>From Email</Label>
                    <Input
                      value={smtpSettings.smtp_from_email}
                      onChange={(e) => setSmtpSettings({...smtpSettings, smtp_from_email: e.target.value})}
                      placeholder="hello@defiancesprings.com"
                      className="bg-stone-800 border-stone-700 text-white focus:border-amber-500"
                      style={{ borderRadius: 0 }}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>From Name</Label>
                    <Input
                      value={smtpSettings.smtp_from_name}
                      onChange={(e) => setSmtpSettings({...smtpSettings, smtp_from_name: e.target.value})}
                      placeholder="The Belonging Sessions"
                      className="bg-stone-800 border-stone-700 text-white focus:border-amber-500"
                      style={{ borderRadius: 0 }}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Admin Notification Emails (comma-separated)</Label>
                  <Input
                    value={Array.isArray(smtpSettings.admin_notification_emails) ? smtpSettings.admin_notification_emails.join(', ') : smtpSettings.admin_notification_emails}
                    onChange={(e) => setSmtpSettings({...smtpSettings, admin_notification_emails: e.target.value})}
                    placeholder="Zazaluvsgaia@gmail.com, macastat@gmail.com"
                    className="bg-stone-800 border-stone-700 text-white focus:border-amber-500"
                    style={{ borderRadius: 0 }}
                  />
                  <p className="text-xs text-stone-500">
                    These addresses will receive notifications for new registrations
                  </p>
                </div>
                <Button
                  onClick={saveSMTPSettings}
                  className="bg-stone-700 text-white hover:bg-stone-600"
                  style={{ borderRadius: 0 }}
                >
                  💾 Save SMTP Settings
                </Button>
              </CardContent>
            </Card>

            <h3 className="text-2xl font-bold mt-12 mb-6">Email Template Settings</h3>
            <p className="text-stone-400 mb-6">
              Customize the automatic email templates sent to users. Use {'{{name}}'}, {'{{attending}}'}, and {'{{event_date}}'} as placeholders.
            </p>
          
            {/* Registration Email Template */}
            <Card style={{ borderRadius: 0 }}>
              <CardHeader>
                <CardTitle className="text-xl">Registration Confirmation Email</CardTitle>
                <p className="text-sm text-stone-400">Sent automatically when a user registers</p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Subject Line</Label>
                  <Input
                    value={emailTemplates.registration?.subject || ''}
                    onChange={(e) => setEmailTemplates({
                      ...emailTemplates,
                      registration: { ...emailTemplates.registration, subject: e.target.value }
                    })}
                    placeholder="Welcome to The Belonging Sessions! 🌱"
                    className="bg-stone-800 border-stone-700 text-white focus:border-amber-500"
                    style={{ borderRadius: 0 }}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email Body (HTML)</Label>
                  <Textarea
                    value={emailTemplates.registration?.body || ''}
                    onChange={(e) => setEmailTemplates({
                      ...emailTemplates,
                      registration: { ...emailTemplates.registration, body: e.target.value }
                    })}
                    placeholder="Enter HTML email template..."
                    className="border-black min-h-[300px] font-mono text-sm"
                    style={{ borderRadius: 0 }}
                  />
                  <p className="text-xs text-stone-500">
                    Available placeholders: {'{{name}}'}, {'{{attending}}'}, {'{{event_date}}'}
                  </p>
                </div>
                <Button
                  onClick={() => saveEmailTemplate('registration')}
                  className="bg-amber-600 text-black hover:bg-black/90"
                  style={{ borderRadius: 0 }}
                >
                  Save Registration Template
                </Button>
              </CardContent>
            </Card>

            {/* Waiting List Email Template */}
            <Card style={{ borderRadius: 0 }}>
              <CardHeader>
                <CardTitle className="text-xl">Waiting List Confirmation Email</CardTitle>
                <p className="text-sm text-stone-400">Sent automatically when someone joins the waiting list</p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Subject Line</Label>
                  <Input
                    value={emailTemplates.waiting_list?.subject || ''}
                    onChange={(e) => setEmailTemplates({
                      ...emailTemplates,
                      waiting_list: { ...emailTemplates.waiting_list, subject: e.target.value }
                    })}
                    placeholder="You're on the Waiting List - The Belonging Sessions"
                    className="bg-stone-800 border-stone-700 text-white focus:border-amber-500"
                    style={{ borderRadius: 0 }}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email Body (HTML)</Label>
                  <Textarea
                    value={emailTemplates.waiting_list?.body || ''}
                    onChange={(e) => setEmailTemplates({
                      ...emailTemplates,
                      waiting_list: { ...emailTemplates.waiting_list, body: e.target.value }
                    })}
                    placeholder="Enter HTML email template..."
                    className="border-black min-h-[300px] font-mono text-sm"
                    style={{ borderRadius: 0 }}
                  />
                  <p className="text-xs text-stone-500">
                    Available placeholders: {'{{name}}'}, {'{{attending}}'}
                  </p>
                </div>
                <Button
                  onClick={() => saveEmailTemplate('waiting_list')}
                  className="bg-amber-600 text-black hover:bg-black/90"
                  style={{ borderRadius: 0 }}
                >
                  Save Waiting List Template
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* LORE Tab */}
        <TabsContent value="lore">
          <LoreContentManager />
        </TabsContent>

        <TabsContent value="ebook">
          <div className="bg-stone-900/80 rounded-xl border border-stone-800 p-6">
            <EbookHighlightManager />
          </div>
        </TabsContent>

        <TabsContent value="announcements">
          <AnnouncementManager />
        </TabsContent>

        <TabsContent value="blog">
          <BlogPostManager token={token} />
        </TabsContent>

        <TabsContent value="resources">
          <ResourcesManager token={token} />
        </TabsContent>

        <TabsContent value="experimental">
          <ExperimentalFeatures />
        </TabsContent>

        </div>
      </section>

      </Tabs>
    </div>
  );
}

export default Admin;



